/* ballot_request_status.js — live receipt status on a ballot request's
   confirmation page. Subscribes to BallotRequestStatusChannel with the page's
   reference and swaps the status line for the one the server sends. The
   server renders the current line first, so the page is right without this.

   Ordering rule: the newest information wins. A broadcast is newer than any
   page re-read that was started before it arrived, so every broadcast (and
   every disconnection or navigation) bumps a generation counter, and a
   re-read only applies if the generation is still the one it started under.
   Without this, a slow re-read answering "sent" could overwrite a "delivered"
   that arrived while it was in flight.

   The state machine is a factory so it can be exercised without a browser
   (spec/v2/javascript/ballot_request_status_test.js). */
(function (root) {
  'use strict';

  var CLASSES = {
    pending:   'rounded-lg border border-gray-200 bg-gray-50 px-5 py-4 text-sm text-gray-700',
    sent:      'rounded-lg border border-sky-200 bg-sky-50 px-5 py-4 text-sm text-sky-900',
    delivered: 'rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900',
    failed:    'rounded-lg border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-900'
  };

  /* box / textEl: the status elements. fetchStatus(): a promise of
     {state, text} read from the server, or a rejection. */
  function createStatusSync(box, textEl, fetchStatus) {
    var generation = 0;

    function apply(state, text) {
      box.dataset.state = state;
      box.className = CLASSES[state] || CLASSES.pending;
      textEl.textContent = text;
    }

    return {
      /* A broadcast: applied at once, and anything older is now stale. */
      received: function (data) {
        if (!data || data.type !== 'receipt_status') return;
        generation += 1;
        apply(data.state, data.text);
      },

      /* On every (re)connection: a broadcast sent between the page rendering
         and the subscription confirming — or while the socket was down — is
         gone for good, so the current line is re-read from the server. */
      connected: function () {
        var mine = generation;
        return fetchStatus()
          .then(function (line) {
            if (mine !== generation) return;   // something newer landed meanwhile
            if (!line) return;
            apply(line.state, line.text);
          })
          .catch(function () {
            // The line on the page stands; the stream continues.
          });
      },

      /* Disconnection or leaving the page: any re-read in flight is stale. */
      invalidate: function () {
        generation += 1;
      },

      generation: function () { return generation; }
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createStatusSync: createStatusSync, CLASSES: CLASSES };
  }

  if (typeof document === 'undefined') return;

  document.addEventListener('turbolinks:load', function () {
    var box = document.getElementById('ballot-request-status');
    if (!box) return;
    if (box.dataset.subscribed === 'true') return;

    var reference = box.dataset.reference;
    var textEl = document.getElementById('ballot-request-status-text');
    if (!reference || !textEl) return;
    if (!root.App || !root.App.cable) return;

    function fetchStatus() {
      return fetch(root.location.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html');
          var freshBox = fresh.getElementById('ballot-request-status');
          var freshText = fresh.getElementById('ballot-request-status-text');
          if (!freshBox || !freshText) return null;
          return { state: freshBox.dataset.state, text: freshText.textContent };
        });
    }

    var sync = createStatusSync(box, textEl, fetchStatus);

    var subscription = root.App.cable.subscriptions.create(
      { channel: 'BallotRequestStatusChannel', reference: reference },
      {
        connected: function () { sync.connected(); },
        disconnected: function () { sync.invalidate(); },
        rejected: function () { sync.invalidate(); },
        received: function (data) { sync.received(data); }
      }
    );

    box.dataset.subscribed = 'true';

    document.addEventListener('turbolinks:before-render', function () {
      sync.invalidate();
      subscription.unsubscribe();
      box.dataset.subscribed = 'false';
    }, { once: true });
  });
})(typeof window !== 'undefined' ? window : this);
